import { useState, useEffect, useMemo } from "react";
import { formatId } from "../functions/functions.js";

/**
 * Hook personalizado para manejar el estado de expansión/colapso de ligas
 * Centraliza la lógica repetida en los componentes de tarjetas deportivas
 * 
 * @param {Array} leagues - Array de ligas con sus datos
 * @returns {Object} Objeto con estado y funciones para manejar ligas
 * @returns {Object} returns.openMap - Mapa de visibilidad de ligas
 * @returns {Function} returns.handleToggle - Función para alternar visibilidad
 * @returns {Array} returns.ids - Array de IDs únicos de ligas
 */
export function useLeagueToggle(leagues) {
  /**
   * Genera IDs únicos para cada liga basados en su nombre
   * Se memoriza para evitar recálculos innecesarios
   */
  const ids = useMemo(() => leagues.map((l) => formatId(l.name)), [leagues]);

  /**
   * Estado para controlar qué ligas están expandidas/colapsadas
   * Por defecto todas las ligas inician abiertas
   */
  const [openMap, setOpenMap] = useState({});

  /**
   * Efecto para inicializar el estado de visibilidad de las ligas
   * Mantiene sincronizado el estado con las ligas disponibles
   */
  useEffect(() => {
    setOpenMap((prev) => {
      const next = { ...prev };
      // Agregar nuevas ligas como abiertas
      ids.forEach((id) => {
        if (!(id in next)) next[id] = true;
      });
      // Remover ligas que ya no existen
      Object.keys(next).forEach((id) => {
        if (!ids.includes(id)) delete next[id];
      });
      return next;
    });
  }, [ids]);

  /**
   * Maneja el toggle de visibilidad de una liga específica
   * @param {string} id - ID único de la liga
   */
  const handleToggle = (id) => {
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return {
    openMap,
    handleToggle,
    ids
  };
}